import React, { useState, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

import type { ILead, IMessageAttachment, IStageHistory, ILeadPropertyInterest } from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';
import type { IProperty, IMessage } from 'src/services/api';
import { ThemeProvider } from 'src/theme';
import Responsive from 'src/components/responsive/Responsive';
import { FlexColumn } from 'src/components/flex';
import Header from 'src/components/lead-sidebar/LeadSidebarHeader';
import PropertyDetails from 'src/components/lead-sidebar/LeadSidebarPropertyDetails';
import UpcomingTour from 'src/components/lead-sidebar/LeadSidebarUpcomingTour';
import PastTours from 'src/components/lead-sidebar/LeadSidebarPastTours';
import Notes from 'src/components/lead-sidebar/LeadSidebarNotes';
import SmartFields from 'src/components/lead-sidebar/LeadSidebarSmartFields';
import ToursFromOtherLeads from 'src/components/lead-sidebar/LeadSidebarToursFromOtherLeads';
import LeadInfo from 'src/components/lead-sidebar/LeadInfo';
import MessageAttachments from 'src/components/lead-sidebar/LeadSidebarMessageAttachments';
import LeadStageTimeline from 'src/components/lead-sidebar/LeadStageTimeline';
import type { IOtherUser } from 'src/api/users-api';
import CreateOrUpdateTourModal from 'src/components/create-or-update-tour-modal';
import { ShowingIcon } from 'src/icons';
import SelectCoapplicantModal from 'src/components/lead-sidebar/SelectCoapplicantModal';
import { useAssociateLeads } from 'src/services/api/leads/use-associate-leads';
import { useUpdateAssociatedLeads } from 'src/services/api/leads/use-update-associated-leads';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import { LeadUtils } from 'src/utils';
import { useNotifications } from 'src/notifications';

import styles from 'src/components/lead-sidebar/LeadSidebar.module.css';

type BaseProps = {
    lead: ILead;
    showings: IShowing[];
    stages: IStageHistory[];
    interests: ILeadPropertyInterest[];
    teamMembers: IOtherUser[];
    isTimelineExpanded?: boolean;
};

type LeadsPageProps = BaseProps & {
    property?: Pick<IProperty, 'id' | 'name'>;
    /**
     * Callback to close the sidebar.
     * If not provided, the close button will not be rendered
     */
    onClose: () => void;
};

type LeadMessagesPageProps = BaseProps & {
    property?: IProperty;
    messages: IMessage[];
    setReplyInput: (replyInput: string) => void;
    attachments?: IMessageAttachment[];
};

const isLeadMessagesPageProps = (props: Props): props is LeadMessagesPageProps => {
    return (props as LeadMessagesPageProps).messages !== undefined;
};

type Props = LeadsPageProps | LeadMessagesPageProps;

export default function LeadSidebar(props: Props) {
    const { lead, showings, stages, interests, teamMembers, isTimelineExpanded } = props;
    const { leads, loggedInUser } = useAuthenticatedState();
    const { addNotification } = useNotifications();

    const [tab, setTab] = useState<'general' | 'tours'>('general');
    const [isTourModalOpen, setIsTourModalOpen] = useState(false);
    const [isCoapplicantModalOpen, setIsCoapplicantModalOpen] = useState(false);

    const { mutateAsync: associateLeads } = useAssociateLeads();
    const { mutateAsync: updateAssociatedLeads } = useUpdateAssociatedLeads();

    useEffect(() => {
        setTab('general');
    }, [lead?.uuid]);

    const coapplicants = useMemo(() => {
        // Use the coapplicants array if it exists on the lead
        if (lead.coapplicants && Array.isArray(lead.coapplicants)) {
            return lead.coapplicants;
        }
        // Otherwise, filter from the leads list
        if (!lead.coapplicant_leads_group_id || !leads) {
            return [];
        }
        return leads.filter(
            (l) => l.coapplicant_leads_group_id === lead.coapplicant_leads_group_id && l.uuid !== lead.uuid
        );
    }, [lead.coapplicants, lead.coapplicant_leads_group_id, lead.uuid, leads]);

    const validSmartFields = Object.entries(lead.smart_fields_data || {})
        .filter(([key, value]) => {
            if (typeof key !== 'string') {
                return false;
            } else if (lead.ingestion_source !== 'apartmentlist' && typeof value !== 'string') {
                // apartmentlist has a different format for smart fields
                return false;
            } else if (key.startsWith('_')) {
                return false;
            } else if (key === 'listing_name' && !!lead.property_id) {
                return false;
            }

            return true;
        });

    return (
        <ThemeProvider>
            <Responsive desktop>
                <div>
                    <FlexColumn className={clsx(styles.root, 'prettyScrollbar')}>
                        <Header lead={lead} onClose={(props as LeadsPageProps).onClose} />

                        <FlexColumn p={1} pt={0} pr={0} m="0px 6px">
                            <PropertyDetails property={props.property} />

                            {coapplicants.length > 0 && (
                                <FlexColumn rowGap={1} mb={1}>
                                    <Typography variant="body2" fontWeight="300" sx = {{ color: '#6B7280' }}>
                                        Co-applicants:
                                    </Typography>
                                    <FlexColumn component="ul" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                        {coapplicants.map((coapplicant) => {
                                            const leadName = LeadUtils.getLeadName(coapplicant);
                                            return (
                                                <Tooltip key={coapplicant.uuid} title={leadName}>
                                                    <Typography
                                                        component="li"
                                                        variant="body2"
                                                        fontWeight="500"
                                                        className="ellipsisText"
                                                    >
                                                        {leadName}
                                                    </Typography>
                                                </Tooltip>
                                            );
                                        })}
                                    </FlexColumn>
                                </FlexColumn>
                            )}
                            <Button
                                variant="outlined"
                                onClick={() => setIsCoapplicantModalOpen(true)}
                                color="primary"
                                fullWidth
                                disabled={lead.owner !== loggedInUser.id}
                            >
                                {coapplicants.length > 0 ? 'Edit co-applicants' : 'Add co-applicant'}
                            </Button>

                            <SelectCoapplicantModal
                                open={isCoapplicantModalOpen}
                                onClose={() => setIsCoapplicantModalOpen(false)}
                                onSelect={(leadsUuids) => {
                                    if (leadsUuids.length > 0 && !leadsUuids.includes(lead.uuid)) {
                                        addNotification('Please include the current lead when selecting coapplicants.', 'error');
                                        return;
                                    }

                                    setIsCoapplicantModalOpen(false);
                                    // If editing an existing group, use the update endpoint
                                    if (lead.coapplicant_leads_group_id) {
                                        updateAssociatedLeads({
                                            group_id: lead.coapplicant_leads_group_id,
                                            lead_uuids: leadsUuids,
                                        });
                                    } else {
                                        // If adding a new group, use the associate endpoint
                                        associateLeads({
                                            lead_uuids: leadsUuids,
                                        });
                                    }
                                }}
                                currentLeadUuid={lead.uuid}
                                existingCoapplicantUuids={coapplicants.map(c => c.uuid)}
                            />

                            <Tabs
                                variant="fullWidth"
                                value={tab}
                                onChange={(_, newTab) => setTab(newTab)}
                                data-testid="lead-sidebar-tabs"
                            >
                                <Tab
                                    label="General"
                                    value="general"
                                    data-testid="general-tab"
                                />
                                <Tab
                                    label="Tours"
                                    value="tours"
                                    data-testid="tours-tab"
                                />
                            </Tabs>

                            <FlexColumn className={styles.sections}>
                                {tab === 'general' && (<>

                                    <Notes key={lead.uuid} lead={lead} />

                                    {!!validSmartFields.length &&
                                        <SmartFields lead={lead} validSmartFields={validSmartFields} />
                                    }

                                    <LeadInfo lead={lead} />

                                    <LeadStageTimeline lead={lead} stages={stages} interests={interests} teamMembers={teamMembers} isExpanded={isTimelineExpanded} />

                                    {isLeadMessagesPageProps(props) && !!props.attachments &&
                                        <MessageAttachments attachments={props.attachments} />
                                    }
                                </>)}
                                {tab === 'tours' && (<>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setIsTourModalOpen(true)}
                                        color="primary"
                                        fullWidth
                                        className={styles.scheduleTourButton}
                                    >
                                        <ShowingIcon width={15} height={15} />
                                        &nbsp;
                                        Schedule a Tour
                                    </Button>
                                    <UpcomingTour showings={showings} lead={lead} />
                                    <PastTours showings={showings} lead={lead} />
                                    <ToursFromOtherLeads showings={showings} lead={lead} />
                                </>)}
                                {isTourModalOpen && (
                                    <CreateOrUpdateTourModal
                                        open={isTourModalOpen}
                                        onClose={() => setIsTourModalOpen(false)}
                                        leads={[lead]}
                                        selectedLeadUuid={lead.uuid}
                                        landlordUserId={lead.owner}
                                        initPropertyId={lead.property_id ?? null}
                                    />)}
                            </FlexColumn>
                        </FlexColumn>
                    </FlexColumn>
                </div>
            </Responsive>
        </ThemeProvider>
    );
}
